'use client';

import {
  LOCATION_SCRUBBED_EVENT,
  announceLocationScrubbed,
  currentLocationHash,
} from '@/utils/trackingLocationGuard';
import { usePathname, useSearchParams } from 'next/navigation';
import { useLayoutEffect, useSyncExternalStore } from 'react';

const HASH_EVENTS = ['hashchange', 'popstate', LOCATION_SCRUBBED_EVENT];

function subscribeToHash(onChange: () => void): () => void {
  for (const name of HASH_EVENTS) window.addEventListener(name, onChange);
  return () => {
    for (const name of HASH_EVENTS) window.removeEventListener(name, onChange);
  };
}

/**
 * The current query + fragment, derived SYNCHRONOUSLY in the render that
 * uses it, so a guard never decides on a stale location:
 *
 * - the query comes from the Next router (`useSearchParams`), which is in
 *   step with the render — on a soft navigation to `/x?token=abc` the new
 *   query is here in the same render, before `window.location` catches up;
 * - the fragment, which the router does not track, comes from
 *   `window.location.hash` through `useSyncExternalStore`, re-read on
 *   `hashchange`, `popstate` and `LOCATION_SCRUBBED_EVENT` (a page's
 *   replaceState strip, which raises no event of its own).
 *
 * The server snapshot has no fragment (''). Uses `useSearchParams`: render
 * the caller inside a `<Suspense>` boundary.
 */
export function useLocationTail(): string {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hash = useSyncExternalStore(
    subscribeToHash,
    currentLocationHash,
    () => ''
  );
  // A soft navigation fires no `hashchange`, and Next writes the new URL to
  // history only after render, so this render's hash can be the previous
  // page's. Re-read it after commit (history is current by then): the event
  // makes every subscriber re-read `window.location.hash`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname and searchParams are re-read triggers
  useLayoutEffect(() => {
    announceLocationScrubbed();
  }, [pathname, searchParams]);
  const search = searchParams?.toString() ?? '';
  return `${search ? `?${search}` : ''}${hash}`;
}
